/**
 * ListCard Component
 *
 * Displays a single favorites list as a card with cover image,
 * name, property count, and hover actions for edit/delete.
 * Supports drag-and-drop via @hello-pangea/dnd props.
 *
 * Features:
 * - Gradient fallback when no cover image (uses list color)
 * - Image loading state with skeleton/shimmer
 * - Aspect ratio consistency (aspect-[3/4])
 * - Hover effects: scale-[1.02], shadow increase
 * - Default list shows heart icon badge, no edit/delete buttons
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FavoriteList } from '../../hooks/useFavorites';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Adjusts a hex color's brightness by a percentage
 * @param color - Hex color string (e.g., '#004D40')
 * @param percent - Negative to darken, positive to lighten
 * @returns Adjusted hex color
 */
function adjustColorBrightness(color: string, percent: number): string {
  // Remove # if present
  const hex = color.replace(/^#/, '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Adjust brightness
  const adjust = (value: number) => {
    const adjusted = Math.round(value * (1 + percent / 100));
    return Math.max(0, Math.min(255, adjusted));
  };

  // Convert back to hex
  const toHex = (value: number) => value.toString(16).padStart(2, '0');

  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
}

// ============================================================================
// Types
// ============================================================================

export interface ListCardProps {
  list: FavoriteList;
  isDefault: boolean;
  coverImageUrl?: string | null;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isDragging?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
}

// ============================================================================
// Component
// ============================================================================

export default function ListCard({
  list,
  isDefault,
  coverImageUrl,
  onClick,
  onEdit,
  onDelete,
  isDragging = false,
  dragHandleProps,
}: ListCardProps) {
  const { t, i18n } = useTranslation('favorites');
  const isRTL = i18n.dir() === 'rtl';

  // Image loading state
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const propertyCount = list.propertyIds.length;
  const propertyCountText =
    propertyCount === 1
      ? t('favorites.lists.propertyCountSingular')
      : t('favorites.lists.propertyCount', { count: propertyCount });

  const listColor = list.color || '#004D40';
  const listIcon = list.icon || 'ri-heart-line';

  // Gradient fallback: from list color to darker shade
  const fallbackGradient = `linear-gradient(135deg, ${listColor} 0%, ${adjustColorBrightness(listColor, -30)} 100%)`;

  // Image event handlers
  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoading(false);
    setImageError(true);
  }, []);

  // Determine if we should show the gradient fallback
  const showGradientFallback = !coverImageUrl || imageError;

  return (
    <div
      className={`group relative bg-white rounded-2xl border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 ease-out ${
        isDragging
          ? 'shadow-2xl scale-105 z-50 ring-2 ring-[#004D40]'
          : 'hover:shadow-lg hover:border-[#1B7566]/40 hover:scale-[1.02]'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`${list.name} - ${propertyCountText}`}
    >
      {/* Cover Image / Gradient Background - aspect-[3/4] for portrait orientation */}
      <div className="relative aspect-[3/4] overflow-hidden">
        {/* Gradient Background (shown when no image or loading) */}
        {showGradientFallback && (
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{ background: fallbackGradient }}
          >
            {/* Large centered icon for gradient cards */}
            <div className="absolute inset-0 flex items-center justify-center">
              <i
                className={`${listIcon} text-6xl text-white/30`}
              ></i>
            </div>
          </div>
        )}

        {/* Cover Image */}
        {coverImageUrl && !imageError && (
          <>
            {/* Skeleton/Shimmer while loading */}
            {imageLoading && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]" />
            )}
            <img
              src={coverImageUrl}
              alt={list.name}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
                imageLoading ? 'opacity-0' : 'opacity-100 group-hover:scale-105'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {/* Dark overlay for better text readability */}
            {!imageLoading && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            )}
          </>
        )}

        {/* Icon Badge (top-left) */}
        <div
          className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-10 h-10 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-110`}
          style={{ backgroundColor: listColor }}
        >
          <i className={`${listIcon} text-xl text-white`}></i>
        </div>

        {/* Default List Heart Badge (only for default list) */}
        {isDefault && (
          <div
            className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center shadow-lg`}
          >
            <i className="ri-heart-fill text-white text-sm"></i>
          </div>
        )}

        {/* Drag Handle (if provided and not default list) */}
        {dragHandleProps && !isDefault && (
          <div
            {...dragHandleProps}
            className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} w-8 h-8 rounded-lg bg-white/80 backdrop-blur-sm flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity z-10`}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ri-draggable text-gray-500"></i>
          </div>
        )}

        {/* Property Count Badge */}
        <div
          className={`absolute bottom-3 ${isRTL ? 'left-3' : 'right-3'} bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full`}
        >
          {propertyCountText}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <h3
            className={`font-semibold text-gray-900 truncate flex-1 group-hover:text-[#004D40] transition-colors ${
              isRTL ? 'text-right' : ''
            }`}
          >
            {list.name}
          </h3>

          {/* Edit/Delete Actions (not for default list) */}
          {!isDefault && (onEdit || onDelete) && (
            <div
              className={`flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                isRTL ? 'mr-2' : 'ml-2'
              }`}
            >
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="p-1.5 text-gray-400 hover:text-[#004D40] hover:bg-[var(--pv-color-surface-sunken)] rounded-lg transition-colors"
                  title={t('favorites.actions.edit')}
                  aria-label={t('favorites.actions.edit')}
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('favorites.actions.delete')}
                  aria-label={t('favorites.actions.delete')}
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Default List Indicator */}
        {isDefault && (
          <span
            className={`inline-flex items-center gap-1 mt-2 text-xs text-[#004D40] bg-[var(--pv-color-surface-sunken)] px-2 py-0.5 rounded-full ${
              isRTL ? 'flex-row-reverse' : ''
            }`}
          >
            <i className="ri-star-fill text-[10px]"></i>
            {t('favorites.lists.defaultName')}
          </span>
        )}
      </div>
    </div>
  );
}
